import { useNavigate } from "@tanstack/react-router";
import { PMButton } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import type { Location, Reminder, Visit } from "@/data/queries";
import { fetchForecast, sky, sprayWindows, tempU, windU, useAsync } from "@/data/weather";
import { LocationMap } from "@/ui/LocationMap";
import { clock } from "@/ui/format";

// The morning briefing: one paragraph an advisor can read in ten seconds,
// every number in it a link, beside the ground it describes.
export function HomeHero({ name, review, due, live, farms, fields }: { name: string; review: Visit[]; due: Reminder[]; live: Visit[]; farms: Location[]; fields: (Location & { last_visit?: string | null; parent_label?: string | null })[] }) {
  const { teamId, units, hierarchy } = useApp();
  const nav = useNavigate();
  // Focus on where the work is: the farm of the latest visit, and fields near
  // the team's centre of gravity (a far-away outlier shouldn't zoom the map out).
  const pts = fields.filter((l) => l.latitude && l.longitude);
  const med = (xs: number[]) => xs.sort((a, b) => a - b)[Math.floor(xs.length / 2)] ?? 0;
  const mLat = med(pts.map((p) => +p.latitude!)), mLng = med(pts.map((p) => +p.longitude!));
  const near = pts.filter((p) => Math.abs(+p.latitude! - mLat) < 2.5 && Math.abs(+p.longitude! - mLng) < 2.5);
  const recentField = fields.find((l) => l.id === [...live, ...review][0]?.map_feature_id) as (Location & { parent_label?: string | null }) | undefined;
  const centre = farms.find((f) => f.label === recentField?.parent_label && f.latitude) ?? farms.find((f) => f.latitude && f.longitude);
  const f = useAsync(centre ? () => fetchForecast(+centre.latitude!, +centre.longitude!, units) : null, [centre?.id, units]);
  const spray = f.data ? sprayWindows(f.data)[0] : null;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const words = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
  const n = (x: number, cap = false) => { const w = x <= 10 ? words[x] : String(x); return cap ? w[0].toUpperCase() + w.slice(1) : w; };
  const walking = live.filter((v) => !v.completed_at);

  const go = (to: string, search?: Record<string, string>) => nav({ to: `/t/$teamId/${to}`, params: { teamId }, search } as any);
  const mapItems = near.slice(0, 60).map((l) => ({ id: l.id, label: l.label || "", geometry: l.geometry, latitude: l.latitude, longitude: l.longitude, tone: walking.some((v) => v.map_feature_id === l.id) ? "warning" as const : "primary" as const }));

  return (
    <section className="hero">
      <div className="hero-text">
        <p className="hero-date">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
        <h1 className="hero-greet">{greet}, {name}.</h1>
        <p className="hero-brief">
          {review.length ? <><button className="hero-link" onClick={() => go("visits", { status: "review" })}>{n(review.length, true)} {review.length === 1 ? "report is" : "reports are"}</button> waiting for a signature</> : <>Every report is signed</>}
          {", "}
          {due.length ? <><button className="hero-link" onClick={() => go("tasks", { scope: "active" })}>{n(due.length)} {due.length === 1 ? "task is" : "tasks are"} due</button></> : <>no tasks are due</>}
          {walking.length ? <>, and <button className="hero-link" onClick={() => go("visits", { status: "active" })}>{walking.length === 1 ? `${walking[0].location_label} has a visit open` : `${n(walking.length)} visits are open in the field`}</button></> : null}.
          {spray && centre ? <> The spray window at {centre.label} opens at {clock(spray.start)} and lasts {spray.hours} hours.</> : null}
        </p>
        <div className="hero-actions">
          {review.length > 0 && <PMButton onClick={() => go("visits", { status: "review" })}>Sign {review.length} {review.length === 1 ? "report" : "reports"}</PMButton>}
          <PMButton variant="outline" onClick={() => go("tasks", { scope: "mine" })}>My tasks</PMButton>
        </div>
      </div>
      <div className="hero-map">
        {mapItems.length > 0 && <LocationMap items={mapItems} height="100%" interactive={false} />}
        {f.data && centre && (
          <div className="hero-wx pm-glass">
            <span className="hero-wx-sky">{sky(f.data.now.code).emoji}</span>
            <div>
              <strong>{Math.round(f.data.now.temp)}{tempU(units)} at {centre.label}</strong>
              <span>{sky(f.data.now.code).label}, wind {Math.round(f.data.now.wind)} {windU(units)}</span>
            </div>
          </div>
        )}
        <span className="hero-map-cap pm-glass">{near.length} {hierarchy.levels.at(-1)!.plural.toLowerCase()}</span>
      </div>
    </section>
  );
}
